/**
 * Back-in-stock capture — routes to Klaviyo instead of the store inbox.
 *
 * WHY THIS EXISTS
 * The back-in-stock snippet renders a Shopify `{% form 'contact' %}`. That emails the
 * store inbox and creates NO Klaviyo subscriber and NO back-in-stock subscription, so
 * the F70 Back-In-Stock flow had zero sends across 90 days — nothing was ever
 * subscribed to it. Eleven call sites across PDPs, bundles and the quiz results page
 * were discarding the highest-intent capture on the site.
 *
 * WHAT IT DOES
 * Intercepts submit and calls Klaviyo's client-side back-in-stock endpoint, which
 * both records the subscription and triggers the restock notification flow. The
 * company id is Klaviyo's PUBLIC key — it is designed to be exposed client-side and
 * is the same value already present in the onsite script tag.
 *
 * PROGRESSIVE ENHANCEMENT
 * Without JS, or if the request fails, the native Shopify contact form still submits
 * as before. A customer never loses their request because our fetch failed.
 *
 * Marketing consent is handled separately and only when the inline notice is shown —
 * a restock request is not by itself consent to marketing.
 */
(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'Smtcx4';
  var KLAVIYO_REVISION = '2026-07-15';
  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

  function variantCatalogId(variantId) {
    // Klaviyo catalog variant id format: integrationType:::catalogId:::externalId
    return '$shopify:::$default:::' + String(variantId);
  }

  function setStatus(form, message, isError) {
    var region = form.querySelector('[data-bis-status]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'field rte text-sm';
      region.setAttribute('data-bis-status', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      form.appendChild(region);
    }
    region.textContent = message;
    region.hidden = false;
    if (isError) region.setAttribute('data-bis-error', '');
    else region.removeAttribute('data-bis-error');
  }

  function subscribe(form) {
    var variantId = form.getAttribute('data-bis-variant-id');
    var emailInput = form.querySelector('input[type="email"]');
    if (!variantId || !emailInput || !emailInput.value) return null;

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: emailInput.value.trim().toLowerCase() }
            }
          }
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: variantCatalogId(variantId) }
          }
        }
      }
    };

    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    });
  }

  function onSubmit(event) {
    var form = event.currentTarget;
    if (form.getAttribute('data-bis-submitting') === 'true') return;

    var variantId = form.getAttribute('data-bis-variant-id');
    if (!variantId) return; // no variant context — let the native form handle it

    event.preventDefault();
    form.setAttribute('data-bis-submitting', 'true');

    var pending = subscribe(form);
    if (!pending) {
      form.removeAttribute('data-bis-submitting');
      form.submit();
      return;
    }

    var settled = false;
    // Never strand the customer on a slow network: fall back to the native submit.
    var timer = window.setTimeout(function () {
      if (settled) return;
      settled = true;
      form.removeAttribute('data-bis-submitting');
      form.submit();
    }, 8000);

    pending
      .then(function (response) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        form.removeAttribute('data-bis-submitting');

        if (response && (response.ok || response.status === 202)) {
          setStatus(form, form.getAttribute('data-bis-success')
            || "You're on the list. We'll email you the moment it's back.", false);
          var email = form.querySelector('input[type="email"]');
          if (email) email.value = '';
          document.dispatchEvent(new CustomEvent('anatomy:back-in-stock-subscribed', {
            detail: { variantId: variantId }
          }));
          return;
        }
        // Klaviyo rejected it — fall back rather than lose the request.
        form.submit();
      })
      .catch(function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        form.removeAttribute('data-bis-submitting');
        form.submit();
      });
  }

  function bind(root) {
    (root || document).querySelectorAll('form[data-bis-variant-id]').forEach(function (form) {
      if (form.getAttribute('data-bis-bound') === 'true') return;
      form.setAttribute('data-bis-bound', 'true');
      form.addEventListener('submit', onSubmit);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(document); });
  } else {
    bind(document);
  }

  // Theme editor replaces section DOM wholesale; rebind on section load.
  document.addEventListener('shopify:section:load', function (event) {
    bind(event.target);
  });
})();
